/**
 * Component is used to configure the password.
 */
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CommonService } from '../../../common/services/common.service';
import { AuthService } from '../../services/auth.service';
import { CustomValidatorService } from '../../../shared/services/custom-validator.service';
import { pattern } from '../../../../../public/assets/pattern';
import { ApiResponse } from '../../../common/models/common.model';
import { ConfigurePasswordReq } from '../../models/auth.model';
/**
 * Component is used to configure the password.
 */
@Component({
  selector: 'app-configure-password',
  imports: [
    FormsModule,
    CommonModule,
    ReactiveFormsModule
  ],
  templateUrl: './configure-password.html',
  styleUrl: './configure-password.scss'
})
export class ConfigurePassword {
  /**
   * Variable which is used to hold all subscriptions for cleanup on component destroy.
   */
  private readonly subscriptions: Subscription = new Subscription;
  /**
   * Variable which is used to store all client messages such as error, success, warning, info, etc.,
   */
  public messages: Record<string, string> = {};
  /**
   * Variable used to store the token.
   */
  public token!: string;
  /**
   * Variable which is used to create reactive forms for creating new password details.
   */
  public configurePasswordForm!: UntypedFormGroup;
  /**
   * Variable which is used to store whether the token is expired or not.
   */
  public isToken!: boolean;
  /**
   * Variable which is used to store the employee id.
   */
  private customerId!: string;
  /**
   * Variable which is used to store the error the message when token is expired.
   */
  public errorMessage!: string;
  /**
   * Variable which is used to define whether to display the loading or not.
   */
  public isLoading: boolean = false;
  /**
   * Variable which is used to define whether to show password or not.
   */
  public showNewPassword: boolean = false;
  /**
   * Variable which is used to define whether to show password or not.
   */
  public showConfirmPassword: boolean = false;
  /**
   * Component constructor which is used to inject the required services.
   * @param authService To access the functions inside the AuthService.
   * @param router To manage navigation.
   * @param route To get the current activated route and route params. (if used for getting params value)
   * @param customValidatorService To access the functions inside the CustomValidatorService.
   * @param commonService To access the functions inside the CommonService.
   */
  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private customValidatorService: CustomValidatorService,
    private commonService: CommonService) { }
  /**
   * Component OnInit life cycle hook.
   */
  public ngOnInit(): void {
    this.subscriptions.add(this.route.params.subscribe((params: Params) => {
      if (params.id) {
        this.customerId = this.commonService.getParam(params.id);
      }
      if (params.token) {
        this.token = params.token;
        this.authService.verifyToken({ token: this.token }).subscribe({
          next: (res: ApiResponse<null>) => {
            this.isToken = res.success;
          },
          error: (err: HttpErrorResponse) => {
            this.isToken = false;
            if (err?.error?.error?.message) {
              console.log('Error: ', err.error.error.message);
              this.errorMessage = err.error.error.message;
            }
          }
        });
      }
    }));
    this.configurePasswordForm = new UntypedFormGroup({
      newPassword: new UntypedFormControl(null, [Validators.required, Validators.pattern(pattern.passwordPattern)]),
      confirmPassword: new UntypedFormControl(null, Validators.required)
    }, this.customValidatorService.validateAreEqual.bind(this));
    this.messages = this.authService.errorMessages;
  }
  /**
   * Method which is used to update the password to the employee details.
   */
  public onSave(): void {
    this.configurePasswordForm.markAllAsTouched();
    if (this.configurePasswordForm.valid) {
      this.isLoading = true;
      const data: ConfigurePasswordReq = {
        password: this.configurePasswordForm.value.newPassword,
        confirmPassword: this.configurePasswordForm.value.confirmPassword,
        token: this.token
      };
      this.subscriptions.add(this.authService.configurePassword(data).subscribe({
        next: (res: ApiResponse<null>) => {
          if (res.success) {
            this.isLoading = false;
            this.router.navigate(['signin']);
          }
        },
        error: (err: HttpErrorResponse) => {
          if (err && err.error && err.error.error && err.error.error.message) {
            this.isLoading = false;
            console.log('Error: ', err.error.error.message);
          }
        }
      }));
    }
  }
  /**
   * Method which is used to define whether to show password or not.
   */
  public toggleNewPassword(): void {
    this.showNewPassword = !this.showNewPassword;
  }
  /**
   * Method which is used to define whether to show password or not.
   */
  public toggleConfirmPassword(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }
  /**
   * Component OnDestroy life cycle hook.
   * And unsubscribe all the subscriptions in the component.
   */
  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
